import React, { ForwardRefRenderFunction } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import ko from "date-fns/locale/ko";
import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";

registerLocale("ko", ko);

type Props = {
  value?: Date;
  minuteStep?: number;
  onChange: (value: Date) => void;
  onClick?: () => void;
  postfix?: React.ReactNode;
  className?: string;
  minDate?: Date;
  error?: string;
};

const CommonDatetimeInput: ForwardRefRenderFunction<HTMLInputElement, Props> = (
  { value, onChange, onClick, postfix, minDate, minuteStep = 10, error },
  ref
) => {
  const roundMinutes = (date: Date) => {
    const timeRoundingCoefficient = 1000 * 60 * minuteStep;
    return new Date(
      Math.round(date.getTime() / timeRoundingCoefficient) *
        timeRoundingCoefficient
    );
  };

  return (
    <>
      <div className="relative text-base" onClick={onClick}>
        <DatePicker
          selected={value}
          onChange={(date: Date) => onChange(roundMinutes(date))}
          dateFormat="yyyy-MM-dd HH:mm"
          timeFormat="HH:mm"
          showTimeSelect
          timeIntervals={minuteStep}
          locale="ko"
          className={clsx([
            "w-[calc(100vw-102px)] pl-12 pr-2 border border-gray-300 rounded-md py-3 h-bottom-button",
            { "border-brand-primary-magenta": error },
            // { "border-red-500": error },
          ])}
          onFocus={(e) => {
            e.target.blur();
          }}
          popperPlacement="top-end"
          minDate={minDate}
        />
        <div className="absolute left-4 top-1/2 -translate-y-1/2">
          <DatepickerCalendarIcon />
        </div>
        {postfix && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            {postfix}
          </div>
        )}
      </div>

      {error && (
        <div className="text-brand-primary-magenta text-xs px-4 pt-1">
          *{error}
        </div>
      )}
    </>
  );
};

export const DatepickerCalendarIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.75977 8.92258H20.247"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.1078 12.5351H16.1163"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M12.0033 12.5351H12.0118"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M7.89 12.5351H7.89857"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M16.1078 16.1301H16.1163"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M12.0033 16.1302H12.0118"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M7.89 16.1302H7.89857"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M15.74 2.07361V5.11759"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      d="M8.26733 2.07361V5.11759"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.3249 3.5343H3.6748V20.5736H20.3249V3.5343Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
    />
  </svg>
);

export default React.forwardRef(CommonDatetimeInput);
